import { Card, CardContent, Slider, Tooltip, Typography } from '@mui/material';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { useAuth, useUserSession } from 'hooks';
import { efficiencyTooltips } from 'lib/constants';
import { updateUserSession } from 'lib/firebase';
import React, { useEffect, useState } from 'react';
import { useLazyGetTrendSavingCalculationsDataQuery } from 'store/services/efficiency';
import { EfficiencyTrendUserSession } from 'types/User/firebaseTypes';
import styles from './TrendData.module.scss';

export interface CalculatedTrendData {
    deliveryAvoided: number;
    deliveryCount: number;
    deliveryAverage: number;
    deviceCount: number;
    moreRevenue: number;
}

const defaultCalcTrendData = {
    deliveryAvoided: 0,
    deliveryCount: 0,
    deliveryAverage: 0,
    deviceCount: 0,
    moreRevenue: 0,
};

interface TrendSavings {
    savedPerMonth: number;
    savedPerYear: number;
    savedPerTank: number;
}

const defaultSavings = {
    savedPerMonth: 0,
    savedPerYear: 0,
    savedPerTank: 0,
};

interface TrendDataProps {
    minDate: Date;
    supplierId: string;
    deliverySizeTarget: number;
    setDeliverySizeTarget: React.Dispatch<React.SetStateAction<number>>;
    monthDiff: number;
}

export const TrendData = ({
    minDate,
    deliverySizeTarget,
    setDeliverySizeTarget,
    supplierId,
    monthDiff,
}: TrendDataProps) => {
    const session = useUserSession();

    const [costPerDelivery, setCostPerDelivery] = useState<number>(
        session?.efficiencyTrend?.costPerDelivery ?? 60
    );
    const [calculatedTrendData, setCalculatedTrendData] =
        useState<CalculatedTrendData>(defaultCalcTrendData);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [savings, setSavings] = useState<TrendSavings>(defaultSavings);
    const { user } = useAuth();

    const [getDataQuery] = useLazyGetTrendSavingCalculationsDataQuery();

    useEffect(() => {
        const newCostPerDelivery = session?.efficiencyTrend?.costPerDelivery;
        const newDeliverySizeTarget =
            session?.efficiencyTrend?.deliverySizeTarget;

        if (newCostPerDelivery && newDeliverySizeTarget) {
            setCostPerDelivery(newCostPerDelivery ?? 60);
            setDeliverySizeTarget(newDeliverySizeTarget ?? 0);
        }
    }, [
        session?.efficiencyTrend?.costPerDelivery,
        session?.efficiencyTrend?.deliverySizeTarget,
        setDeliverySizeTarget,
    ]);

    // Load trend data.
    useEffect(() => {
        if (supplierId !== undefined) {
            setIsLoading(true);
            getDataQuery({
                supplierId: supplierId,
                startDate: minDate,
            })
                .then((result) => {
                    const { data } = result;
                    if (data) {
                        setCalculatedTrendData({
                            ...data,
                            deliveryAvoided: Math.round(data.deliveryAvoided),
                        });
                    }
                })
                .finally(() => setIsLoading(false));
        }
    }, [minDate, getDataQuery, supplierId]);

    // Calculate + set savings data.
    useEffect(() => {
        const savedPerMonth =
            (costPerDelivery * calculatedTrendData.deliveryAvoided) / monthDiff;
        const savedPerYear = savedPerMonth * 12;
        const savedPerTank = savedPerYear / calculatedTrendData.deviceCount;
        setSavings({
            savedPerMonth,
            savedPerYear,
            savedPerTank,
        });
    }, [calculatedTrendData, monthDiff, costPerDelivery]);

    const handleUserSessionSave = () => {
        if (user?.uid) {
            const newSessionEntry: EfficiencyTrendUserSession = {
                costPerDelivery,
                deliverySizeTarget,
            };
            updateUserSession(user.uid, 'efficiencyTrend', newSessionEntry);
        }
    };

    return (
        <div className={styles.container} data-testid="TrendData">
            <Card className={styles.sliderCard} data-testid="TrendCard">
                <Tooltip
                    title={
                        <span style={{ fontSize: 'large' }}>
                            {efficiencyTooltips['costPerDelivery']}
                        </span>
                    }
                    arrow
                    placement="top"
                >
                    <Typography
                        color="text.secondary"
                        className={styles.textWithHelp}
                        sx={{ marginLeft: '1rem', width: 'fit-content' }}
                    >
                        Delivery Cost Estimate
                    </Typography>
                </Tooltip>
                <CardContent className={styles.costSlider}>
                    <Typography sx={{ fontSize: 'large' }}>
                        ${costPerDelivery}
                    </Typography>
                    <Slider
                        min={0}
                        max={250}
                        value={costPerDelivery}
                        onChange={(
                            event: Event,
                            newValue: number | number[]
                        ) => {
                            if (typeof newValue === 'number') {
                                setCostPerDelivery(newValue);
                            }
                        }}
                    />
                </CardContent>
            </Card>
            <Card className={styles.sliderCard} data-testid="TrendCard">
                <Tooltip
                    title={
                        <span style={{ fontSize: 'large' }}>
                            {efficiencyTooltips['deliveryTarget']}
                        </span>
                    }
                    arrow
                    placement="top"
                >
                    <Typography
                        color="text.secondary"
                        className={styles.textWithHelp}
                        sx={{ marginLeft: '1rem', width: 'fit-content' }}
                    >
                        Delivery Size Target
                    </Typography>
                </Tooltip>
                <CardContent className={styles.costSlider}>
                    <Typography sx={{ fontSize: 'large' }}>
                        {deliverySizeTarget}%
                    </Typography>
                    <Slider
                        min={0}
                        max={100}
                        value={deliverySizeTarget}
                        onChange={(
                            event: Event,
                            newValue: number | number[]
                        ) => {
                            if (typeof newValue === 'number') {
                                setDeliverySizeTarget(newValue);
                            }
                        }}
                        onChangeCommitted={handleUserSessionSave}
                    />
                </CardContent>
            </Card>
            <div className={styles.dataCards}>
                <Card className={styles.card} data-testid="TrendCard">
                    <Typography variant="h6" className={styles.cardHeader}>
                        Trends
                    </Typography>
                    <div className={styles.row}>
                        <Typography color="text.secondary">
                            Total Deliveries
                        </Typography>
                        {isLoading ? (
                            <LoadingSpinner size={12} />
                        ) : (
                            <Typography>
                                {calculatedTrendData.deliveryCount.toLocaleString()}
                            </Typography>
                        )}
                    </div>
                    <div className={styles.row}>
                        <Typography color="text.secondary">
                            Deliveries Avoided
                        </Typography>
                        {isLoading ? (
                            <LoadingSpinner size={12} />
                        ) : (
                            <Typography>
                                {Math.round(
                                    calculatedTrendData.deliveryAvoided
                                ).toLocaleString()}
                            </Typography>
                        )}
                    </div>
                    <div className={styles.row}>
                        <Typography color="text.secondary">
                            Tanks Visited
                        </Typography>

                        {isLoading ? (
                            <LoadingSpinner size={12} />
                        ) : (
                            <Typography>
                                {calculatedTrendData.deviceCount.toLocaleString()}
                            </Typography>
                        )}
                    </div>
                    <div className={styles.row}>
                        <Tooltip
                            title={
                                <span style={{ fontSize: 'large' }}>
                                    {efficiencyTooltips['deliveryAverage']}
                                </span>
                            }
                            arrow
                            placement="top"
                        >
                            <Typography
                                color="text.secondary"
                                className={styles.textWithHelp}
                            >
                                Avg. Delivery Size
                            </Typography>
                        </Tooltip>
                        {isLoading ? (
                            <LoadingSpinner size={12} />
                        ) : (
                            <Typography>
                                {Math.round(
                                    calculatedTrendData.deliveryAverage
                                ) + '%'}
                            </Typography>
                        )}
                    </div>
                </Card>
                <Card className={styles.card} data-testid="TrendCard">
                    <Typography variant="h6" className={styles.cardHeader}>
                        Savings
                    </Typography>
                    <div className={styles.row}>
                        <Tooltip
                            title={
                                <span style={{ fontSize: 'large' }}>
                                    {efficiencyTooltips['savedPerMonth']}
                                </span>
                            }
                            arrow
                            placement="top"
                        >
                            <Typography
                                color="text.secondary"
                                className={styles.textWithHelp}
                            >
                                Monthly Savings
                            </Typography>
                        </Tooltip>
                        <Typography>
                            $
                            {!isNaN(savings?.savedPerMonth)
                                ? Math.round(
                                      savings?.savedPerMonth
                                  ).toLocaleString()
                                : 0}
                        </Typography>
                    </div>
                    <div className={styles.row}>
                        <Typography color="text.secondary">
                            Annual Savings
                        </Typography>
                        <Typography>
                            $
                            {!isNaN(savings?.savedPerYear)
                                ? Math.round(
                                      savings?.savedPerYear
                                  ).toLocaleString()
                                : 0}
                        </Typography>
                    </div>
                    <div className={styles.row}>
                        <Tooltip
                            title={
                                <span style={{ fontSize: 'large' }}>
                                    {efficiencyTooltips['savedPerTank']}
                                </span>
                            }
                            arrow
                            placement="top"
                        >
                            <Typography
                                color="text.secondary"
                                className={styles.textWithHelp}
                            >
                                Savings Per Tank
                            </Typography>
                        </Tooltip>
                        <Typography>
                            $
                            {!isNaN(savings?.savedPerTank)
                                ? Math.round(
                                      savings?.savedPerTank
                                  ).toLocaleString()
                                : 0}
                            /yr
                        </Typography>
                    </div>
                    <div className={styles.row}>
                        <Tooltip
                            title={
                                <span style={{ fontSize: 'large' }}>
                                    {efficiencyTooltips['moreRevenue']}
                                </span>
                            }
                            arrow
                            placement="top"
                        >
                            <Typography
                                color="text.secondary"
                                className={styles.textWithHelp}
                            >
                                Revenue Increase Per Delivery
                            </Typography>
                        </Tooltip>
                        <Typography>
                            {!isNaN(calculatedTrendData?.moreRevenue)
                                ? Math.round(
                                      calculatedTrendData?.moreRevenue
                                  ).toLocaleString()
                                : 0}
                            %
                        </Typography>
                    </div>
                </Card>
            </div>
        </div>
    );
};
